/**
 * Escape HTML special characters to prevent XSS attacks.
 * Converts HTML entities in strings for safe DOM insertion.
 *
 * @param {string} text - Raw text to escape
 * @returns {string} Escaped HTML-safe string (e.g., "<div>" becomes "&lt;div&gt;")
 * @example
 * escapeHtml("<script>alert('xss')</script>")
 * // => "&lt;script&gt;alert(&#39;xss&#39;)&lt;/script&gt;"
 */
export function escapeHtml(text) {
  if (typeof text !== 'string') return text;
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Normalize a Shopify price to a dollar amount. Numbers (and digit-only
 * strings) are treated as cents; strings containing a decimal point are
 * treated as an already-in-dollars amount.
 *
 * @param {number|string} cents - Price in cents, or a decimal-string dollar amount
 * @returns {number} Dollar amount
 */
function centsToDollars(cents) {
  return typeof cents === 'string' && cents.includes('.')
    ? parseFloat(cents)
    : cents / 100;
}

/**
 * Format money amount to USD currency string, always with 2 decimal places
 * and thousands separators. Shopify stores prices in cents.
 *
 * @param {number|string} cents - Price amount in cents (or dollar-decimal string)
 * @returns {string} Formatted USD string (e.g., "$1,234.56")
 * @example
 * formatMoney(123456)  // => "$1,234.56"
 * formatMoney("12.34") // => "$12.34"
 */
export function formatMoney(cents) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(centsToDollars(cents));
}

/**
 * Format a Shopify price (cents int, or dollar string) to a locale currency string.
 * Drops trailing ".00" when the amount has no cents.
 *
 * @param {number|string} cents - Price in cents, or a decimal-string dollar amount
 * @returns {string} Formatted USD string (e.g., "$12.34" or "$12")
 * @example
 * formatPrice(1234)   // => "$12.34"
 * formatPrice(1200)   // => "$12"
 */
export function formatPrice(cents) {
  const amount = centsToDollars(cents);
  const hasCents = Math.round(amount * 100) % 100 !== 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: hasCents ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Whether the user has requested reduced motion. Checked live (not cached) so it
 * responds to a runtime preference change without re-initialising callers.
 *
 * @returns {boolean}
 */
export function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Read and cache the parsed [data-theme-settings] JSON object.
 * Returns an empty object if the element is absent or unparseable.
 *
 * @returns {Record<string, unknown>}
 */
let _themeSettings = null;
export function getThemeSettings() {
  if (_themeSettings) return _themeSettings;
  try {
    const el = document.querySelector('[data-theme-settings]');
    _themeSettings = el ? JSON.parse(el.textContent) : {};
  } catch {
    _themeSettings = {};
  }
  return _themeSettings;
}

/**
 * Wire drag-to-scroll on a horizontal thumbnail viewport.
 * Handles mouse and touch, snaps to the nearest item on release,
 * and sets a `dragged` flag so click handlers can ignore post-drag clicks.
 *
 * @param {HTMLElement} viewport - The scrollable thumbnail container
 * @param {object} [options]
 * @param {(v: boolean) => void} [options.setDragged] - Set dragged flag on owner
 * @returns {() => void} Teardown that removes every listener this call added.
 *   Callers that re-wire the same persistent viewport (e.g. the UGC gallery
 *   reloading after a variant morph) must invoke it before re-binding, or
 *   handlers stack on the element and fire multiple times per drag.
 */
export function initThumbDrag(viewport, { setDragged = () => {} } = {}) {
  if (!viewport) return () => {};

  let isDragging = false;
  let prevPageX = 0;
  let prevScrollLeft = 0;
  let positionDiff = 0;

  const dragStart = (e) => {
    isDragging = true;
    setDragged(false);
    prevPageX = e.pageX ?? e.touches?.[0]?.pageX ?? 0;
    prevScrollLeft = viewport.scrollLeft;
    viewport.style.cursor = 'grabbing';
  };

  const dragging = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    positionDiff = (e.pageX ?? e.touches?.[0]?.pageX ?? 0) - prevPageX;
    if (Math.abs(positionDiff) > 4) setDragged(true);
    viewport.scrollLeft = prevScrollLeft - positionDiff;
  };

  const dragStop = () => {
    if (!isDragging) return;
    isDragging = false;
    viewport.style.cursor = 'grab';

    // Snap to nearest item based on item width + 8px gap (gap-2). Some callers
    // (e.g. product gallery) wrap items in a [data-thumb-track], so measure a
    // real item inside the track rather than the full-width track wrapper —
    // otherwise step ≈ whole strip and every drag rounds back to the front.
    const itemsParent = viewport.querySelector('[data-thumb-track]') || viewport;
    const firstItem = itemsParent.firstElementChild;
    const step = firstItem ? firstItem.offsetWidth + 8 : 1;
    const snapped = Math.round(viewport.scrollLeft / step) * step;
    viewport.scrollTo({ left: snapped, behavior: 'smooth' });

    setTimeout(() => setDragged(false), 50);
  };

  const preventDrag = (e) => e.preventDefault();

  viewport.addEventListener('mousedown', dragStart);
  viewport.addEventListener('mousemove', dragging);
  viewport.addEventListener('mouseup', dragStop);
  viewport.addEventListener('mouseleave', dragStop);
  viewport.addEventListener('touchstart', dragStart, { passive: true });
  viewport.addEventListener('touchmove', dragging, { passive: false });
  viewport.addEventListener('touchend', dragStop);
  viewport.addEventListener('touchcancel', dragStop);
  viewport.addEventListener('dragstart', preventDrag);

  return () => {
    viewport.removeEventListener('mousedown', dragStart);
    viewport.removeEventListener('mousemove', dragging);
    viewport.removeEventListener('mouseup', dragStop);
    viewport.removeEventListener('mouseleave', dragStop);
    viewport.removeEventListener('touchstart', dragStart);
    viewport.removeEventListener('touchmove', dragging);
    viewport.removeEventListener('touchend', dragStop);
    viewport.removeEventListener('touchcancel', dragStop);
    viewport.removeEventListener('dragstart', preventDrag);
  };
}

/** @type {WeakMap<HTMLElement, ReturnType<typeof setTimeout>>} Tracks per-message reveal timers. */
const skuCopiedTimers = new WeakMap();

/**
 * Copy the SKU text to the clipboard and surface a transient "Copied!" cue.
 * Bound to the `[data-product-sku]` button (Enter/Space work natively).
 *
 * @param {MouseEvent} event - Click event from the SKU element
 * @param {HTMLElement} element - Section root containing `[data-sku-copied-message]`
 */
export async function onCopySkuClick(event, element) {
  const sku = event.currentTarget?.textContent?.trim();
  if (!sku) return;

  try {
    await navigator.clipboard.writeText(sku);
    showSkuCopied(element);
  } catch (error) {
    console.warn("Failed to copy SKU to clipboard:", error);
  }
}

/**
 * Reveal the `[data-sku-copied-message]` live region briefly so both sighted
 * and screen-reader users get confirmation that the SKU was copied.
 *
 * @param {HTMLElement} element - Section root containing `[data-sku-copied-message]`
 */
export function showSkuCopied(element) {
  const messageEl = element.querySelector("[data-sku-copied-message]");
  if (!messageEl) return;

  messageEl.hidden = false;
  clearTimeout(skuCopiedTimers.get(messageEl));
  skuCopiedTimers.set(
    messageEl,
    setTimeout(() => {
      messageEl.hidden = true;
    }, 2000),
  );
}
